/**
 * Theme model + token derivation.
 *
 * A theme is authored as a small set of semantic "base roles" (Background, Text,
 * Accent, Rise, Fall, Buy, Sell, Warning). `deriveTokens` expands those into the
 * full set of semantic CSS variables the app consumes.
 *
 * Design decisions (see docs/superpowers/specs/2026-07-24-theme-editor-design.md):
 *  - Built-in themes (light/dark/black) stay STATIC in index.css — derivation only
 *    powers custom themes and seeds the editor. Exact reproduction of the built-ins
 *    is a non-goal; derivation targets a coherent, accessible result.
 *  - Rise/Fall are per-theme (green-up/red-down can differ light vs dark).
 *  - Buy/Sell are aliased to Rise/Fall in v1 (no --buy/--sell tokens emitted yet).
 *  - Theme-invariant categorical palettes (--chart-series-*, --agg-*, --ticker-*,
 *    --link-*) are NOT derived — a custom theme applies as overrides on top of a
 *    base mode class, so those inherit unchanged.
 */
import { z } from 'zod'
import {
  type Hsl,
  parseHslToken,
  formatHslToken,
  hexToHsl,
  hslToHex,
  adjustLightness,
  mix,
  pickReadableForeground,
} from './color'

export type ThemeMode = 'dark' | 'light'

/** The user-editable semantic roles. Values are hex strings (editor-friendly). */
export interface BaseRoles {
  background: string
  foreground: string
  /** brand / interactive accent → --primary */
  accent: string
  /** up / gain / bullish → --positive */
  rise: string
  /** down / loss / bearish → --negative */
  fall: string
  /** order buy-side; v1 aliases to rise when unset */
  buy?: string
  /** order sell-side; v1 aliases to fall when unset */
  sell?: string
  warning: string
}

export const BASE_ROLE_KEYS: (keyof BaseRoles)[] = [
  'background',
  'foreground',
  'accent',
  'rise',
  'fall',
  'buy',
  'sell',
  'warning',
]

/** Buy defaults to Rise until a dedicated order-entry UI needs its own token. */
export const effectiveBuy = (r: BaseRoles): string => r.buy ?? r.rise
/** Sell defaults to Fall until a dedicated order-entry UI needs its own token. */
export const effectiveSell = (r: BaseRoles): string => r.sell ?? r.fall

/** Base-role seeds matching the static built-in themes in index.css. */
const hex = (hslToken: string): string => hslToHex(parseHslToken(hslToken))

export const BUILTIN_BASE_ROLES: Record<'light' | 'dark' | 'black', BaseRoles> = {
  // Edge Light is the "Worky" palette (mostly-white, indigo brand). These roles
  // are the seed for index.css :root — regenerate that block via
  // resolveThemeTokens() if you change them (they must stay in sync).
  light: {
    background: '#F1F2F7',
    foreground: '#15172A',
    accent: '#4E5BE6',
    // Deeper green/red/amber than the dark themes: the luminous versions wash
    // out on a light ground (< 3:1). See the preset contrast audit.
    rise: '#087F43',
    fall: '#DC2626',
    warning: '#B45309',
  },
  dark: {
    background: hex('228 29% 14%'),
    foreground: hex('240 5% 92%'),
    accent: hex('229 95% 75%'),
    rise: hex('147 100% 38%'),
    fall: hex('0 100% 62%'),
    warning: hex('38 92% 50%'),
  },
  black: {
    background: hex('228 25% 8%'),
    foreground: hex('220 15% 90%'),
    accent: hex('229 95% 70%'),
    rise: hex('147 100% 38%'),
    fall: hex('0 100% 62%'),
    warning: hex('38 92% 50%'),
  },
}

/** All semantic token keys derivation emits (without the leading `--`). */
export const DERIVED_TOKEN_KEYS = [
  'background',
  'reading-pane',
  'foreground',
  'card',
  'card-foreground',
  'popover',
  'popover-foreground',
  'primary',
  'primary-foreground',
  'secondary',
  'secondary-foreground',
  'muted',
  'muted-foreground',
  'accent',
  'accent-foreground',
  'hover',
  'primary-hover',
  'secondary-hover',
  'destructive-hover',
  'background-hover',
  'destructive',
  'destructive-foreground',
  'border',
  'border-strong',
  'input',
  'field',
  'ring',
  'sidebar',
  'sidebar-foreground',
  'nav-rail',
  'nav-rail-foreground',
  'positive',
  'negative',
  'warning',
  'chart-canvas',
  'chart-text',
  'chart-text-muted',
  'chart-grid',
  'chart-crosshair',
  'chart-tooltip-bg',
  'chart-candle-up',
  'chart-candle-down',
  'grid-header-bg',
  'grid-bg',
  'grid-row-alt',
  'grid-row-hover',
  'grid-row-selected',
] as const

export type DerivedTokenKey = (typeof DERIVED_TOKEN_KEYS)[number]

/** Classify a base-role set into a mode by its background lightness. */
export function deriveMode(roles: BaseRoles): ThemeMode {
  return hexToHsl(roles.background).l < 50 ? 'dark' : 'light'
}

/**
 * Signed lightness offsets (from background) for elevated/recessed surfaces.
 *
 * Dividers are drawn ON raised surfaces (card / popover at +9), so a border
 * token must clear that, not just the background: `border` at +7 or `muted`
 * at +7 vanishes inside a dropdown. `border` (+11) is the plain-canvas
 * hairline; `border-strong` (+20) is the divider for popovers, menus and
 * sheets (house rule). Chris, 2026-09-12: derived dark themes had dividers
 * he could not see in the filter-config dropdowns.
 */
const SURFACE_L: Record<ThemeMode, Record<string, number>> = {
  dark: {
    'reading-pane': 4,
    card: 9,
    popover: 9,
    secondary: 15,
    muted: 7,
    accent: 12,
    border: 11,
    'border-strong': 20,
    input: 7,
    sidebar: 4,
    'nav-rail': 4,
    'background-hover': 5,
    'chart-canvas': -4,
    'grid-header-bg': 18,
    'grid-bg': 8,
    'grid-row-alt': 6,
    'grid-row-hover': 12,
  },
  light: {
    'reading-pane': 0,
    card: 4,
    popover: 4,
    secondary: -5,
    muted: -5,
    accent: -5,
    border: -9,
    'border-strong': -21,
    input: -9,
    sidebar: -4,
    'nav-rail': -4,
    'background-hover': -5,
    'chart-canvas': 0,
    'grid-header-bg': -3,
    'grid-bg': 4,
    'grid-row-alt': 1,
    'grid-row-hover': -1,
  },
}

/**
 * Expand base roles into the full semantic token set.
 * Returns a map of `token-name` → value string (HSL triplet, or a raw rgba for
 * `hover`) ready to write as `--token-name: <value>`.
 */
export function deriveTokens(roles: BaseRoles): Record<string, string> {
  const mode = deriveMode(roles)
  const isDark = mode === 'dark'

  const background = hexToHsl(roles.background)
  const foreground = hexToHsl(roles.foreground)
  const accent = hexToHsl(roles.accent)
  const positive = hexToHsl(roles.rise)
  const negative = hexToHsl(roles.fall)
  const warning = hexToHsl(roles.warning)

  // High/low-contrast foreground candidates for "pick readable text on X".
  const lightText: Hsl = { h: foreground.h, s: Math.min(foreground.s, 8), l: 96 }
  const darkText: Hsl = { h: background.h, s: Math.min(background.s, 20), l: 12 }

  const surfaceL = SURFACE_L[mode]
  const surface = (key: string): Hsl => adjustLightness(background, surfaceL[key])

  const card = surface('card')
  const sidebar = surface('sidebar')
  const secondary = surface('secondary')
  const border = surface('border')
  const mutedForeground = mix(foreground, background, 0.28)

  const chartCanvas = surface('chart-canvas')
  const canvasToken = formatHslToken(chartCanvas)
  // Dark chart grounds (canvas lightness below 50) take the blue-gray
  // crosshair; see the `chart-crosshair` note.
  const isDarkCanvas = chartCanvas.l < 50

  const destructive: Hsl = {
    h: negative.h,
    s: Math.min(Math.max(negative.s, 40), 88),
    l: 58,
  }
  const sidebarForeground = pickReadableForeground(sidebar, lightText, darkText)

  const out: Record<string, string> = {
    background: formatHslToken(background),
    'reading-pane': formatHslToken(surface('reading-pane')),
    foreground: formatHslToken(foreground),

    card: formatHslToken(card),
    'card-foreground': formatHslToken(foreground),
    popover: formatHslToken(surface('popover')),
    'popover-foreground': formatHslToken(foreground),

    primary: formatHslToken(accent),
    'primary-foreground': formatHslToken(pickReadableForeground(accent, lightText, darkText)),
    ring: formatHslToken(accent),
    'primary-hover': formatHslToken(adjustLightness(accent, isDark ? 5 : -6)),

    secondary: formatHslToken(secondary),
    'secondary-foreground': formatHslToken(foreground),
    'secondary-hover': formatHslToken(adjustLightness(secondary, isDark ? 5 : -5)),

    muted: formatHslToken(surface('muted')),
    'muted-foreground': formatHslToken(mutedForeground),

    accent: formatHslToken(surface('accent')),
    'accent-foreground': formatHslToken(foreground),

    hover: isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.04)',
    'background-hover': formatHslToken(surface('background-hover')),

    destructive: formatHslToken(destructive),
    'destructive-foreground': formatHslToken(
      pickReadableForeground(destructive, lightText, darkText),
    ),
    'destructive-hover': formatHslToken(adjustLightness(destructive, isDark ? 5 : -6)),

    border: formatHslToken(border),
    'border-strong': formatHslToken(surface('border-strong')),
    input: formatHslToken(surface('input')),
    // Fill for inputs sitting ON a panel — derived from `card`, NOT from
    // `background`, because that relationship is the whole point: the field has
    // to read against the surface it is on. Deriving it from the canvas breaks
    // on very light themes, where `card` clamps at 100% and silently collapses
    // the step to nothing. Dark mode raises the field off its panel; light mode
    // steps it down, since the panel is already at white.
    field: formatHslToken(adjustLightness(card, isDark ? 3 : -3)),

    sidebar: formatHslToken(sidebar),
    'sidebar-foreground': formatHslToken(sidebarForeground),
    'nav-rail': formatHslToken(surface('nav-rail')),
    'nav-rail-foreground': formatHslToken(sidebarForeground),

    positive: formatHslToken(positive),
    negative: formatHslToken(negative),
    warning: formatHslToken(warning),

    'chart-canvas': canvasToken,
    'chart-text': formatHslToken(foreground),
    'chart-text-muted': formatHslToken(mix(foreground, background, 0.45)),
    'chart-grid': formatHslToken(border),
    // Crosshair is canvas-driven: every dark ground gets a blue-gray (#606a85,
    // Chris 2026-08-14; widened from the Edge Dark ground to all dark canvases
    // 2026-08-29), light canvases keep the neutral gray (#525252, the
    // 3rd-darkest swatch gray). The derived mutedForeground read as a light,
    // harsh line on dark presets. Same "fixed chart value in derivation"
    // precedent as chart-candle-up/down below. Built-in dark themes apply via
    // their index.css classes (this covers derived themes).
    'chart-crosshair': isDarkCanvas ? '224 16% 45%' : '0 0% 32%',
    'chart-tooltip-bg': formatHslToken(card),
    // Chart candles use fixed teal/red that match the OHLC chg% readout
    // (utils/ohlcChange CHANGE_UP/CHANGE_DOWN), deliberately DECOUPLED from the
    // Rise/Fall (positive/negative) roles so the app's green/red +accents
    // (scanner cells, +/-% badges) keep the theme's colors while the chart
    // candles/OHLC read this specific teal/red (Chris, 2026-08-04).
    'chart-candle-up': formatHslToken(hexToHsl('#22c55e')),
    'chart-candle-down': formatHslToken(hexToHsl('#ef4444')),

    'grid-header-bg': formatHslToken(surface('grid-header-bg')),
    'grid-bg': formatHslToken(surface('grid-bg')),
    'grid-row-alt': formatHslToken(surface('grid-row-alt')),
    'grid-row-hover': formatHslToken(surface('grid-row-hover')),
    'grid-row-selected': formatHslToken({
      h: accent.h,
      s: accent.s * 0.85,
      l: isDark ? 42 : 84,
    }),
  }

  return out
}

// ── Persisted custom-theme shape ────────────────────────────────────────────

const hexColor = z.string().regex(/^#[0-9a-fA-F]{6}$/, { message: 'Expected hex like "#191d2d"' })
const hslToken = z.string().regex(/^\d{1,3}\s\d{1,3}%\s\d{1,3}%$/, {
  message: 'Expected HSL token like "228 29% 14%"',
})

export const BaseRolesSchema = z.object({
  background: hexColor,
  foreground: hexColor,
  accent: hexColor,
  rise: hexColor,
  fall: hexColor,
  buy: hexColor.optional(),
  sell: hexColor.optional(),
  warning: hexColor,
})

export const CustomThemeSchema = z.object({
  id: z.string(),
  name: z.string().min(1),
  /** which built-in mode class the overrides layer on top of */
  baseMode: z.enum(['light', 'dark', 'black']),
  roles: BaseRolesSchema,
  /** optional per-token Advanced overrides: token-name → HSL triplet */
  overrides: z.record(z.string(), hslToken).optional(),
})

export type CustomTheme = z.infer<typeof CustomThemeSchema>

/** Final token map for a custom theme: derived roles with Advanced overrides applied. */
export function resolveCustomThemeTokens(theme: CustomTheme): Record<string, string> {
  const base = deriveTokens(theme.roles)
  return { ...base, ...(theme.overrides ?? {}) }
}
